'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const steps = [
  {
    number: '01',
    title: 'Contact EarnestBridge',
    body: 'Reach out once you have a signed Letter of Intent. No application needed until your LOI is in hand.',
    time: 'LOI Signed',
    phase: 'Inquiry',
  },
  {
    number: '02',
    title: 'Apply & Get Approved',
    body: 'Complete the EMD Program application and receive approval while negotiating your Purchase and Sale Agreement.',
    time: 'During PSA',
    phase: 'Review',
  },
  {
    number: '03',
    title: 'We Fund Your EMD',
    body: 'Once the PSA is signed, we wire your EMD directly to an approved title company. Your capital stays free.',
    time: 'PSA Signed',
    phase: 'Funding',
  },
  {
    number: '04',
    title: 'Exercise EMD Option',
    body: 'Exercise your EMD Option before the Due Diligence Period expires. We only fund soft deposits and retain the right to terminate prior to the EMD going hard.',
    time: 'Before DD Expires',
    phase: 'Resolution',
  },
];

// Dark section color tokens
const DARK_BG = '#0D1F17';
const DARK_CARD = 'rgba(255,255,255,0.04)';
const DARK_CARD_ACTIVE = 'rgba(255,255,255,0.07)';
const DARK_BORDER = 'rgba(255,255,255,0.10)';
const DARK_BORDER_SUBTLE = 'rgba(255,255,255,0.06)';
const DARK_NODE_ACTIVE_BORDER = 'rgba(201,168,76,0.55)';
const DARK_NODE_INACTIVE_BORDER = 'rgba(255,255,255,0.15)';
const GOLD = '#C9A84C';
const GOLD_DIM = 'rgba(201,168,76,0.70)';
const GOLD_FAINT = 'rgba(201,168,76,0.35)';
const TEXT_PRIMARY = '#F0EDE6';
const TEXT_MUTED = 'rgba(240,237,230,0.60)';
const TEXT_SUBTLE = 'rgba(240,237,230,0.38)';

export default function HowItWorksPreview() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('[data-reveal]');
            els.forEach((el, i) => {
              const element = el as HTMLElement;
              setTimeout(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
                element.style.filter = 'blur(0)';
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="how-it-works-preview"
      className="py-16 lg:py-20 px-6 relative z-10"
      style={{ background: DARK_BG }}
      aria-label="How the EMD funding process works"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          data-reveal
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 lg:mb-16"
          style={{
            opacity: 0,
            transform: 'translateY(24px)',
            filter: 'blur(4px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-px" style={{ background: GOLD_FAINT }} />
              <p
                className="section-label"
                style={{ color: GOLD_DIM, letterSpacing: '0.22em', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase' }}
              >
                The Process
              </p>
            </div>
            <h2
              id="hiw-preview-heading"
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                color: TEXT_PRIMARY,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              Four steps from
              <br />
              <span className="italic" style={{ color: GOLD }}>
                inquiry to funded.
              </span>
            </h2>
          </div>
          <Link
            href="/emd-program"
            className="text-[10px] font-bold uppercase tracking-[0.22em] whitespace-nowrap transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{
              color: GOLD,
              borderBottom: `1px solid ${GOLD_FAINT}`,
              paddingBottom: '3px',
              outlineColor: GOLD,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD_FAINT; }}
          >
            Full Process Details →
          </Link>
        </div>

        {/* ── DESKTOP: Horizontal timeline ── */}
        <div
          className="hidden lg:block"
          data-reveal
          style={{ opacity: 0, transform: 'translateY(24px)', filter: 'blur(4px)', transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)' }}
        >
          {/* Phase labels row */}
          <div className="grid grid-cols-4 mb-3">
            {steps.map((step) => (
              <div key={step.number} className="flex items-center justify-center">
                <span
                  className="text-[8px] font-bold uppercase tracking-[0.35em]"
                  style={{ color: GOLD_DIM }}
                >
                  {step.phase}
                </span>
              </div>
            ))}
          </div>

          {/* Timeline track */}
          <div className="relative grid grid-cols-4 mb-10">
            {/* Continuous horizontal line */}
            <div
              className="absolute top-5 left-[12.5%] right-[12.5%] h-px"
              style={{ background: DARK_BORDER }}
            />
            {/* Gold progress line — first 3 segments */}
            <div
              className="absolute top-5 h-px"
              style={{
                left: '12.5%',
                width: '62.5%',
                background: `linear-gradient(to right, ${GOLD_DIM}, ${GOLD_FAINT})`,
              }}
            />

            {steps.map((step, i) => (
              <div key={step.number} className="flex flex-col items-center relative">
                {/* Milestone node */}
                <div
                  className="relative z-10 flex items-center justify-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    border: i < 3
                      ? `1.5px solid ${DARK_NODE_ACTIVE_BORDER}`
                      : `1px solid ${DARK_NODE_INACTIVE_BORDER}`,
                    background: i < 3 ? DARK_CARD_ACTIVE : DARK_CARD,
                  }}
                >
                  <span
                    className="font-display font-bold text-xs"
                    style={{
                      color: i < 3 ? GOLD : TEXT_SUBTLE,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {step.number}
                  </span>
                </div>
                {/* Time label below node */}
                <span
                  className="mt-2 text-[8px] font-semibold uppercase tracking-[0.2em]"
                  style={{ color: TEXT_SUBTLE }}
                >
                  {step.time}
                </span>
              </div>
            ))}
          </div>

          {/* Step content row */}
          <ol
            className="grid grid-cols-4 gap-0"
            style={{ borderTop: `1px solid ${DARK_BORDER_SUBTLE}` }}
          >
            {steps.map((step, i) => (
              <li
                key={step.number}
                className="px-6 pt-8 pb-6"
                style={{
                  borderRight: i < steps.length - 1 ? `1px solid ${DARK_BORDER_SUBTLE}` : 'none',
                  background: i === 2 ? DARK_CARD_ACTIVE : 'transparent',
                }}
              >
                <h3
                  className="font-display font-semibold mb-3 leading-tight"
                  style={{
                    fontSize: '1rem',
                    color: TEXT_PRIMARY,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: TEXT_MUTED }}
                >
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* ── MOBILE: Compact vertical timeline ── */}
        <div className="lg:hidden">
          <div className="relative pl-10">
            {/* Vertical line */}
            <div
              className="absolute left-4 top-3 bottom-3 w-px"
              style={{ background: DARK_BORDER }}
              aria-hidden="true"
            />

            <ol>
              {steps.map((step, i) => (
                <li
                  key={step.number}
                  data-reveal
                  className="relative"
                  style={{
                    opacity: 0,
                    transform: 'translateY(20px)',
                    filter: 'blur(4px)',
                    transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
                    paddingBottom: i < steps.length - 1 ? '28px' : '0',
                  }}
                >
                  {/* Node */}
                  <div
                    className="absolute -left-10 top-0 flex items-center justify-center"
                    style={{
                      width: '28px',
                      height: '28px',
                      border: `1.5px solid ${DARK_NODE_ACTIVE_BORDER}`,
                      background: DARK_CARD_ACTIVE,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    <span
                      className="font-display font-bold"
                      style={{ fontSize: '9px', color: GOLD, letterSpacing: '-0.01em' }}
                    >
                      {step.number}
                    </span>
                  </div>

                  {/* Phase + time */}
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[9px] font-bold uppercase tracking-[0.28em]"
                      style={{ color: GOLD_DIM }}
                    >
                      {step.phase}
                    </span>
                    <span
                      className="text-[9px] font-semibold uppercase tracking-[0.15em]"
                      style={{ color: TEXT_SUBTLE }}
                    >
                      · {step.time}
                    </span>
                  </div>

                  <h3
                    className="font-display font-semibold mb-2"
                    style={{
                      fontSize: '1rem',
                      color: TEXT_PRIMARY,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.25,
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: TEXT_MUTED, maxWidth: '52ch' }}
                  >
                    {step.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}